import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { DeviceViewSettingsTable, IntentRule } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { hasEntityAppliedCustomIntentCheck } from 'components/DeviceView/helpers/hasEntityAppliedCustomIntentCheck';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { type FC, memo, useEffect, useRef, useState } from 'react';
import type Scrollbars from 'react-custom-scrollbars-2';
import { useDeviceViewContent } from '../DeviceViewContentContext';
import {
  DEVICE_ATTRIBUTE_DETAIL_TAB,
  DEVICE_DETAIL_TAB,
  INTENT_CHECK_DETAIL_TAB,
  PACKET_DETAILS_DETAIL_TAB,
  PATH_INSPECTOR_DETAIL_TAB,
  RESOURCE_HIERARCHY_DETAIL_TAB,
  STP_DETAIL_TAB,
} from '../deviceViewConstants';
import {
  type CompareMode,
  type DeviceViewEntity,
  type DeviceViewEntityId,
  type TabDataTables,
  deviceViewPacketDetailsEntityId,
  deviceViewPathlookupDebugEntityId,
  isEdgeEntity,
  isLabelsEntity,
  isNodeEntity,
  isPacketDetailsEntity,
  isPathlookupDebugEntity,
} from '../deviceViewTypes';
import { canOpenDeviceEdgeEntity } from '../helpers/canOpenDeviceEdgeEntity';
import { getDeviceViewEntityTables } from '../helpers/getDeviceViewEntityTables';
import { getRequestSnapshotId } from '../helpers/getRequestSnapshotId';
import { getSelectedTable } from '../helpers/getSelectedTable';
import { hasEntityInvalidEdgeProtocol } from '../helpers/hasEntityInvalidEdgeProtocol';
import { isProtocolStp } from '../helpers/isProtocolStp';
import { DeviceViewContentDetailHeader } from './DeviceViewContentDetailHeader';
import { DeviceViewContentDetailTabContent } from './DeviceViewContentDetailTabContent';
import { DeviceViewContentStpDetailTabContent } from './DeviceViewContentStpDetailTabContent';
import { DeviceViewPacketDetails } from './DeviceViewPacketDetails';
import { DeviceViewPathlookupDebug } from './DeviceViewPathlookupDebug';
import { DeviceViewTable } from './DeviceViewTable/DeviceViewTable';
import { PathInspector } from './PathInspector/PathInspector';

import { ScrollColumnView } from '@ipfabric/ui-components/ScrollView/ScrollColumnView';
import type { RequiredBy } from '@ipfabric/utils/types/RequiredBy';
import { DeviceSnapshotAttributesTable } from 'components/DeviceSnapshotAttributesTable/DeviceSnapshotAttributesTable';
import { LabelsTable } from 'components/DeviceView/Labels/LabelsTable';
import { CloudResourceHierarchyTabContent } from '../CloudConstructView/CloudResourceHierarchyTabContent';
import { useDeviceViewContextSelectors } from '../model/DeviceViewModel';
import { loadDeviceViewData } from '../model/commands/loadDeviceViewData';
import { DeviceViewContentEntityBackground } from './DeviceViewContentEntityBackground';

interface Props {
  activeDeviceViewEntityId: DeviceViewEntityId;
  compareMode?: CompareMode | null;
  deviceViewEntity: DeviceViewEntity;
  hideInactiveEntity?: boolean;
  nameSuffix: string;
  onShowSetupTables: () => void;
}

const canRefreshTabData = (
  deviceViewEntity: DeviceViewEntity,
  tabData: TabDataTables,
) => {
  if (isLabelsEntity(deviceViewEntity)) {
    return false;
  }
  return (
    deviceViewEntity.deviceViewElement !== 'node' ||
    tabData[deviceViewEntity.id] === undefined
  );
};

const BareDeviceViewContentEntity: FC<Props> = ({
  deviceViewEntity,
  ...props
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
}) => {
  const msg = useLocale();
  const scrollbar = useRef<Scrollbars>(null);

  const {
    decisions,
    disableSiteLink,
    hoveredPathInspectorEdge,
    intentRule,
    isolateStpInstances,
    pathInspectorPackets,
    pathLookupPathInspectorSelectedPackets,
    pathLookupPreset,
    showLoader,
    snapshotId,
    snapshotToCompareId,
    stpInstances,
  } = useDeviceViewContent();

  const ctxProps = useDeviceViewContextSelectors({
    deviceViewEntities: prop('deviceViewEntities'),
    dispatch: prop('dispatch'),
    pathsWithName: prop('pathsWithName'),
    settings: prop('settings'),
    tabData: prop('tabData'),
  });

  const selectedTable = getSelectedTable(
    deviceViewEntity,
    ctxProps.settings,
    ctxProps.pathsWithName,
    intentRule,
  );
  const deviceViewEntityTables = getDeviceViewEntityTables(
    deviceViewEntity,
    ctxProps.settings,
    ctxProps.pathsWithName,
  );
  const [dataIsLoading, setDataIsLoading] = useState<boolean>(false);
  const labelsEntity = isLabelsEntity(deviceViewEntity);
  const showEntityLoader = !labelsEntity && (dataIsLoading || showLoader);
  const isActiveDeviceViewEntity =
    deviceViewEntity.id === props.activeDeviceViewEntityId;
  const intentCheckResult =
    isNodeEntity(deviceViewEntity) || isEdgeEntity(deviceViewEntity)
      ? deviceViewEntity.intentCheckResult
      : null;

  const handleReloadEdgeData = useStableCallback(() => {
    if (deviceViewEntity.deviceViewElement === 'edge') {
      ctxProps.dispatch(loadDeviceViewData, {
        deviceViewElement: deviceViewEntity.deviceViewElement,
        deviceViewEntityId: deviceViewEntity.id,
        onDataLoadingChange: setDataIsLoading,
        snapshotId: getRequestSnapshotId(
          deviceViewEntity,
          snapshotId,
          snapshotToCompareId,
        ),
      });
    }
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: intented to be fired only when compared snapshots change
  useEffect(() => {
    if (
      snapshotId !== snapshotToCompareId &&
      canRefreshTabData(deviceViewEntity, ctxProps.tabData)
    ) {
      ctxProps.dispatch(loadDeviceViewData, {
        deviceViewElement: deviceViewEntity.deviceViewElement,
        deviceViewEntityId: deviceViewEntity.id,
        onDataLoadingChange: setDataIsLoading,
        snapshotId: getRequestSnapshotId(
          deviceViewEntity,
          snapshotId,
          snapshotToCompareId,
        ),
      });
    }
  }, [snapshotId, snapshotToCompareId, ctxProps.tabData[deviceViewEntity.id]]);

  if (props.hideInactiveEntity && !isActiveDeviceViewEntity) {
    return null;
  }

  if (isPacketDetailsEntity(deviceViewEntity)) {
    if (pathLookupPreset === undefined) {
      return null;
    }
    return (
      <DeviceViewPacketDetails
        deviceViewEntity={deviceViewEntity}
        key='pathlookup'
        pathLookupPreset={pathLookupPreset}
        visible={
          props.activeDeviceViewEntityId === deviceViewPacketDetailsEntityId
        }
      />
    );
  }

  if (isPathlookupDebugEntity(deviceViewEntity)) {
    return (
      <DeviceViewPathlookupDebug
        deviceViewEntity={deviceViewEntity}
        key='pathlookup-debug'
        visible={
          props.activeDeviceViewEntityId === deviceViewPathlookupDebugEntityId
        }
      />
    );
  }

  if (showEntityLoader && isActiveDeviceViewEntity) {
    return (
      <div
        className='flex flex-1 flex-col justify-center'
        key={deviceViewEntity.id}
      >
        <LoaderAndText />
      </div>
    );
  }

  const showCustomIntentCheckTable = ((
    rule: IntentRule | null,
  ): rule is RequiredBy<IntentRule, 'webEndpoint'> =>
    hasEntityAppliedCustomIntentCheck(intentCheckResult) &&
    rule?.webEndpoint !== undefined)(intentRule);

  const contentHasPadding =
    selectedTable !== PACKET_DETAILS_DETAIL_TAB &&
    selectedTable !== PATH_INSPECTOR_DETAIL_TAB;

  const selectedLabelsTab =
    isNodeEntity(deviceViewEntity) || isLabelsEntity(deviceViewEntity)
      ? deviceViewEntity.labelsTabs.find(
          (tab) => tab.columnKey === selectedTable,
        )
      : undefined;
  const labelsTabIsSelected = selectedLabelsTab !== undefined;
  const tableEntity =
    isEdgeEntity(deviceViewEntity) || isNodeEntity(deviceViewEntity)
      ? deviceViewEntity
      : null;

  return (
    <div
      className={
        props.activeDeviceViewEntityId === deviceViewEntity.id
          ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
          : 'hidden'
      }
    >
      <DeviceViewContentDetailHeader
        nodeType={
          isNodeEntity(deviceViewEntity) ? deviceViewEntity.nodeType : null
        }
        deviceViewElement={deviceViewEntity.deviceViewElement}
        deviceViewEntityId={deviceViewEntity.id}
        deviceViewEntityTables={deviceViewEntityTables}
        extra={isNodeEntity(deviceViewEntity) ? deviceViewEntity.extra : null}
        intentCheckIsApplied={hasEntityAppliedCustomIntentCheck(
          intentCheckResult,
          intentRule,
        )}
        labelsTabs={
          isNodeEntity(deviceViewEntity) || isLabelsEntity(deviceViewEntity)
            ? deviceViewEntity.labelsTabs
            : []
        }
        isPathLookupNode={
          isNodeEntity(deviceViewEntity) && deviceViewEntity.isPathLookupNode
        }
        nodeHasPathInspectorDetail={
          isNodeEntity(deviceViewEntity) &&
          deviceViewEntity.nodeHasPathInspectorDetail
        }
        onShowSetupTables={props.onShowSetupTables}
        parentNodeSn={
          isNodeEntity(deviceViewEntity) ? deviceViewEntity.parentNodeSn : null
        }
        protocol={
          isEdgeEntity(deviceViewEntity) ? deviceViewEntity.protocol : undefined
        }
        selectedTable={selectedTable}
      />
      <ScrollColumnView
        className={labelsTabIsSelected ? 'min-h-0 overflow-hidden' : undefined}
        contentClassName={
          labelsTabIsSelected ? 'min-h-0 overflow-hidden' : undefined
        }
        ref={scrollbar}
      >
        <div
          className={
            labelsTabIsSelected
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
              : 'flex flex-1 flex-col'
          }
        >
          {isNodeEntity(deviceViewEntity) &&
            selectedTable === DEVICE_DETAIL_TAB &&
            !showEntityLoader && (
              <DeviceViewContentEntityBackground
                backgroundColor='gray'
                hasPadding={true}
                hidden={false}
              >
                <DeviceViewContentDetailTabContent
                  deviceViewEntityId={deviceViewEntity.id}
                  disableSiteLink={disableSiteLink}
                />
              </DeviceViewContentEntityBackground>
            )}
          {isNodeEntity(deviceViewEntity) && (
            <DeviceViewContentEntityBackground
              backgroundColor='gray'
              hasPadding={true}
              hidden={selectedTable !== RESOURCE_HIERARCHY_DETAIL_TAB}
            >
              <CloudResourceHierarchyTabContent
                active={selectedTable === RESOURCE_HIERARCHY_DETAIL_TAB}
                deviceViewEntity={deviceViewEntity}
                snapshotId={snapshotId}
              />
            </DeviceViewContentEntityBackground>
          )}
          {(isNodeEntity(deviceViewEntity) ||
            isLabelsEntity(deviceViewEntity)) &&
            !showEntityLoader &&
            deviceViewEntity.labelsTabs.map((labelsTab) => (
              <DeviceViewContentEntityBackground
                backgroundColor='gray'
                className='min-h-0 overflow-hidden'
                hasPadding={false}
                hidden={selectedLabelsTab?.columnKey !== labelsTab.columnKey}
                key={labelsTab.columnKey}
              >
                <LabelsTable
                  active={selectedLabelsTab?.columnKey === labelsTab.columnKey}
                  snapshotId={snapshotId}
                  targetId={labelsTab.id}
                  targetType={labelsTab.type}
                  title={labelsTab.title}
                />
              </DeviceViewContentEntityBackground>
            ))}
          {isNodeEntity(deviceViewEntity) &&
            selectedTable === DEVICE_ATTRIBUTE_DETAIL_TAB &&
            !showEntityLoader && (
              <DeviceViewContentEntityBackground
                backgroundColor='gray'
                hasPadding={false}
                hidden={false}
              >
                <DeviceSnapshotAttributesTable
                  snapshotId={snapshotId}
                  filter={{ sn: ['eq', deviceViewEntity.sn] }}
                />
              </DeviceViewContentEntityBackground>
            )}
          {isNodeEntity(deviceViewEntity) &&
            ctxProps.settings.nodesLoaded &&
            selectedTable === null && (
              <h4 className='flex flex-1 flex-col items-center justify-center'>
                {deviceViewEntity.nodeType === null
                  ? // without adding new flag to deviceViewEntity, nodeType is the only field by which
                    // we can recognize whether the node has defined no tabs or is not present in the snapshot at all.
                    msg.deviceView.deviceNotInSnapshot
                  : msg.deviceView.noTablesToShow}
              </h4>
            )}
          {isEdgeEntity(deviceViewEntity) &&
            isProtocolStp(deviceViewEntity.protocol) &&
            selectedTable === STP_DETAIL_TAB &&
            // Wait for loaded data to prevent table unmount
            deviceViewEntity.loaded &&
            !showEntityLoader && (
              <DeviceViewContentEntityBackground
                backgroundColor='gray'
                hasPadding={false}
                hidden={false}
              >
                <DeviceViewContentStpDetailTabContent
                  deviceViewEntity={deviceViewEntity}
                  isolateStpInstances={isolateStpInstances}
                  nameSuffix={props.nameSuffix}
                  snapshotId={snapshotId}
                  snapshotToCompareId={snapshotToCompareId}
                  stpInstances={stpInstances}
                />
              </DeviceViewContentEntityBackground>
            )}
          {isNodeEntity(deviceViewEntity) &&
            decisions &&
            pathInspectorPackets &&
            selectedTable === PATH_INSPECTOR_DETAIL_TAB &&
            pathLookupPathInspectorSelectedPackets !== undefined &&
            pathLookupPreset !== undefined && (
              <DeviceViewContentEntityBackground
                backgroundColor='gray'
                hasPadding={false}
                hidden={false}
              >
                <PathInspector
                  decisions={decisions}
                  deviceViewEntity={deviceViewEntity}
                  deviceViewEntityNodeSn={deviceViewEntity.sn}
                  hoveredPathInspectorEdge={hoveredPathInspectorEdge}
                  nodeTitle={deviceViewEntity.title}
                  pathInspectorPackets={pathInspectorPackets}
                  pathLookupPathInspectorSelectedPackets={
                    pathLookupPathInspectorSelectedPackets
                  }
                  pathLookupPreset={pathLookupPreset}
                  scrollbar={scrollbar}
                />
              </DeviceViewContentEntityBackground>
            )}
          {showCustomIntentCheckTable && tableEntity !== null && (
            <DeviceViewContentEntityBackground
              backgroundColor='white'
              hasPadding={false}
              hidden={selectedTable !== INTENT_CHECK_DETAIL_TAB}
            >
              <DeviceViewTable
                active={selectedTable === INTENT_CHECK_DETAIL_TAB}
                deviceViewEntity={tableEntity}
                deviceViewEntityId={tableEntity.id}
                filters={tableEntity.tableFilters[intentRule.webEndpoint]!}
                intentRule={intentRule}
                selectedTable={intentRule.webEndpoint}
                snapshotId={snapshotId}
                snapshotToCompareId={snapshotToCompareId}
                sort={tableEntity.tableSort[intentRule.webEndpoint]}
              />
            </DeviceViewContentEntityBackground>
          )}
          {tableEntity !== null &&
            deviceViewEntityTables.map((table: DeviceViewSettingsTable) => (
              <DeviceViewContentEntityBackground
                backgroundColor='white'
                hasPadding={false}
                key={table.webEndpoint}
                hidden={selectedTable !== table.webEndpoint}
              >
                <DeviceViewTable
                  active={table.webEndpoint === selectedTable}
                  deviceViewEntity={tableEntity}
                  deviceViewEntityId={tableEntity.id}
                  filters={tableEntity.tableFilters[table.webEndpoint]!}
                  intentRule={null}
                  selectedTable={table.webEndpoint}
                  snapshotId={snapshotId}
                  snapshotToCompareId={snapshotToCompareId}
                  sort={tableEntity.tableSort[table.webEndpoint]}
                />
              </DeviceViewContentEntityBackground>
            ))}
          {isEdgeEntity(deviceViewEntity) &&
            !deviceViewEntityTables.length &&
            !showCustomIntentCheckTable && (
              <DeviceViewContentEntityBackground
                backgroundColor='gray'
                hasPadding={contentHasPadding}
                className='items-center justify-center'
                hidden={false}
              >
                <LoaderAndText
                  hasError
                  onShowErrorMessage={handleReloadEdgeData}
                  errorText={
                    canOpenDeviceEdgeEntity(deviceViewEntity.protocol) &&
                    hasEntityInvalidEdgeProtocol(deviceViewEntity.edges)
                      ? msg.deviceView.unableToLoadData
                      : undefined
                  }
                />
              </DeviceViewContentEntityBackground>
            )}
        </div>
      </ScrollColumnView>
    </div>
  );
};

export const DeviceViewContentEntity = memo(BareDeviceViewContentEntity);
